import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useApi, useAction } from '../lib/hooks'
import { dateTime, money, num, titleCase } from '../lib/format'
import {
  Banner, ChainNode, Delta, ErrorNote, Loading, Panel, Pill, StatMini, TraceChain,
} from '../ui/kit'
import { BackLink, usePageCrumb } from './breadcrumbs'

interface VarianceSplit { reasonCode: string; label: string | null; deltaKg: number; deltaPct: number; note: string | null }
interface Variance {
  id: string; fromPoint: string; toPoint: string; fromKg: number; toKg: number
  deltaKg: number; deltaPct: number; withinTolerance: boolean
  reasonCode: string; reasonNote: string | null; valueAtRisk: number | null
  closedAt: string | null; splits: VarianceSplit[]
}
interface Trace {
  id: string; code: string; status: string
  farmer: { id: string; code: string; name: string; msisdn: string; zone: string }
  crop: { name: string; code: string }
  grade: { code: string; name: string; spec: string }
  harvestDate: string | null; fifoBatch: string | null; expiresAt: string | null
  pricePerKg: number; priceLockedAt: string; priceOverrideReason: string | null
  farmgateKg: number | null; depotInKg: number | null; deliveredKg: number | null; returnedKg: number
  purchaseValue: number | null; totalVariancePct: number | null; unexplainedPct: number | null
  chain: Array<{
    point: string; netKg: number; grossKg: number; tareKg: number; method: string
    weighedAt: string; weighedBy: { name: string } | null; scale: { code: string | null; cert: string | null } | null
    hasPhoto: boolean; hasGps: boolean
  gpsAccuracyM: number | null; distanceM: number | null
  farFromWhereItShouldBe: boolean; photoUrl: string | null; temperatureC: number | null; scaleDriftFlagged: boolean
  }>
  variances: Variance[]
  payment: {
    id: string; status: string; amount: number; accountRef: string
    providerReceipt: string | null; requestedAt: string; settledAt: string | null
    failureReason: string | null; retryCount: number
  } | null
  rejections: Array<{
    id: string; code: string; rejectedKg: number; reasonCode: string; reasonNote: string | null
    rejectedAt: string; rejectedByName: string | null; returnedAt: string | null
    returnWeightKg: number | null; disposition: string; resoldPricePerKg: number | null
    capturedLoss: number | null; buyer: string
  }>
}

const REASONS = [
  { code: 'MOISTURE', label: 'Moisture / mass loss in transit' },
  { code: 'FOREIGN_MATTER', label: 'Soil, stalk and foreign matter' },
  { code: 'GRADE_OUT', label: 'Graded out at depot' },
  { code: 'SPOILAGE', label: 'Spoiled / rotten' },
  { code: 'HANDLING', label: 'Handling and crushing loss' },
  { code: 'SHORT_DELIVERY', label: 'Short delivered by supplier' },
  { code: 'SCALE_DRIFT', label: 'Scale drift — pending recalibration' },
  { code: 'THEFT_SUSPECTED', label: 'Suspected diversion' },
]

export function LotTrace() {
  const { code } = useParams()
  const { data, error, loading, reload } = useApi<Trace>(code ? `/collections/lots/${code}` : null)

  usePageCrumb(code ? [{ label: code }] : [])

  if (error) return <main className="page"><ErrorNote error={error} onRetry={reload} /></main>
  if (loading || !data) return <main className="page"><Panel><Loading rows={6} /></Panel></main>

  const openVariance = data.variances.find((v) => !v.withinTolerance && v.closedAt === null)
  const nodes: ChainNode[] = data.chain.map((w, i) => {
    const v = data.variances.find((x) => x.toPoint === w.point)
    return {
      point: w.point,
      netKg: w.netKg,
      weighedBy: w.weighedBy,
      weighedAt: w.weighedAt,
      scale: w.scale,
      hasPhoto: w.hasPhoto,
      hasGps: w.hasGps,
      gpsAccuracyM: w.gpsAccuracyM,
      distanceM: w.distanceM,
      farFromWhereItShouldBe: w.farFromWhereItShouldBe,
      photoUrl: w.photoUrl,
      temperatureC: w.temperatureC,
      deltaPct: v?.deltaPct ?? null,
      tone: i === 0 ? 'ok' : v && !v.withinTolerance ? (v.closedAt ? 'warn' : 'crit') : 'ok',
      attributions: v?.splits.map((s) => ({ label: s.label ?? s.reasonCode, deltaPct: s.deltaPct })),
    }
  })

  return (
    <main className="page">
      <div className="page-head">
        <BackLink to="/console/collections" label="All collections" />
        <div className="spread">
          <div>
            <h1 className="mono">{data.code}</h1>
            <p>
              {data.farmer.name} · {data.farmer.zone} · {data.crop.name} Grade {data.grade.code}
              {data.fifoBatch && <> · batch {data.fifoBatch}</>}
            </p>
          </div>
          <div className="row">
            <Pill tone={data.status === 'closed' ? 'ok' : 'info'}>{titleCase(data.status)}</Pill>
            {(data.unexplainedPct ?? 0) > 0 && <Pill tone="crit">Unexplained {data.unexplainedPct}%</Pill>}
          </div>
        </div>
      </div>

      <div className="stack">
        {openVariance && (
          <Banner
            tone="crit"
            title={`${Math.abs(Number(openVariance.deltaPct))}% of the weight is missing — more than allowed`}
          >
            {Math.abs(Number(openVariance.deltaKg)).toFixed(1)} kg unaccounted for
            {openVariance.valueAtRisk ? <>, worth {money(openVariance.valueAtRisk)}</> : null}.
            Assign a reason below to close this lot.
          </Banner>
        )}

        <div className="split">
          <div className="stack">
            <Panel
              title="Who had it, and what it weighed"
              meta="every time it changed hands: the weight, the time, the person and the proof"
            >
              <div className="panel-body">
                <TraceChain nodes={nodes} />
              </div>
            </Panel>

            {openVariance && <AssignVariance variance={openVariance} onDone={reload} />}

            {data.rejections.length > 0 && (
              <Panel title="Linked rejections" meta="the produce has to come back before you credit anyone">
                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Ref</th><th>Buyer</th><th className="n">Rejected</th>
                        <th className="n">Returned</th><th>What happened to it</th><th className="n">Loss recorded</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.rejections.map((r) => (
                        <tr key={r.id}>
                          <td className="code strong">{r.code}</td>
                          <td>{r.buyer}</td>
                          <td className="n">{num(r.rejectedKg, 1)} kg</td>
                          <td className="n">
                            {r.returnWeightKg === null
                              ? <Pill tone="crit" dot={false}>not back</Pill>
                              : `${num(r.returnWeightKg, 1)} kg`}
                          </td>
                          <td>{titleCase(r.disposition)}</td>
                          <td className="n">{money(r.capturedLoss, { currency: false })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            )}
          </div>

          <div className="stack">
            <Panel title="Purchase">
              <div className="panel-body col" style={{ gap: 12 }}>
                <div className="mini-row">
                  <StatMini label="Price agreed" value={money(data.pricePerKg, { currency: false })}
                    note={`per kg at ${dateTime(data.priceLockedAt)}`} />
                  <StatMini label="Paid to farmer" value={money(data.purchaseValue, { compact: true })} />
                </div>
                {data.priceOverrideReason && (
                  <Banner tone="warn" title="Board price was overridden">{data.priceOverrideReason}</Banner>
                )}
                {data.payment ? (
                  <div className="payout">
                    <span className="payout-mark">M</span>
                    <div className="payout-who">
                      <b>{titleCase(data.payment.status)}</b>
                      <i>{data.payment.accountRef}{data.payment.providerReceipt ? ` · ${data.payment.providerReceipt}` : ''}</i>
                    </div>
                    <div className="payout-amt">
                      <b>{money(data.payment.amount, { currency: false })}</b>
                      <span>{data.payment.settledAt ? dateTime(data.payment.settledAt) : 'not paid out yet'}</span>
                    </div>
                  </div>
                ) : (
                  <Banner tone="warn" title="No payment raised yet">
                    This farmer has not been paid for this lot.
                  </Banner>
                )}
                {data.payment?.failureReason && (
                  <Banner tone="crit" title={`Payment failed — retry ${data.payment.retryCount}`}>
                    {data.payment.failureReason}
                  </Banner>
                )}
              </div>
            </Panel>

            <Panel title="Specification" meta={`Grade ${data.grade.code}`}>
              <div className="panel-body">
                <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-2)', lineHeight: 1.6 }}>
                  {data.grade.spec}
                </p>
                <div className="mini-row" style={{ marginTop: 12 }}>
                  <StatMini label="At the farm" value={`${num(data.farmgateKg, 1)} kg`} />
                  <StatMini label="At the store" value={data.depotInKg === null ? '—' : `${num(data.depotInKg, 1)} kg`} />
                  <StatMini label="Delivered" value={data.deliveredKg === null ? '—' : `${num(data.deliveredKg, 1)} kg`} />
                </div>
              </div>
            </Panel>

            <Panel title="Where the weight went">
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr><th>Step</th><th className="n">Change</th><th>Reason</th></tr>
                  </thead>
                  <tbody>
                    {data.variances.map((v) => (
                      <tr key={v.id}>
                        <td className="faint" style={{ fontSize: 'var(--fs-xs)' }}>
                          {v.fromPoint} → {v.toPoint}
                        </td>
                        <td className="n"><Delta value={v.deltaPct} goodDirection="zero" decimals={2} /></td>
                        <td>
                          {v.splits.length > 0
                            ? v.splits.map((s) => (
                                <div key={s.reasonCode} style={{ fontSize: 'var(--fs-xs)' }}>
                                  {s.label ?? s.reasonCode} <span className="faint">{s.deltaPct}%</span>
                                </div>
                              ))
                            : <Pill tone="crit" dot={false}>{titleCase(v.reasonCode)}</Pill>}
                        </td>
                      </tr>
                    ))}
                    {data.variances.length === 0 && (
                      <tr><td colSpan={3} className="faint">Nothing unaccounted for.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      </div>
    </main>
  )
}

/** Splitting a variance across named causes is what makes a lot closeable. */
function AssignVariance({ variance, onDone }: { variance: Variance; onDone: () => void }) {
  const total = Math.abs(Number(variance.deltaKg))
  const [splits, setSplits] = useState<Array<{ reasonCode: string; deltaKg: string }>>([
    { reasonCode: 'MOISTURE', deltaKg: total.toFixed(1) },
  ])
  const [note, setNote] = useState('')

  const assigned = splits.reduce((s, x) => s + (Number(x.deltaKg) || 0), 0)
  const remaining = total - assigned
  const balanced = Math.abs(remaining) < 0.011

  const save = useAction(async () => {
    await api.post(`/collections/variances/${variance.id}/assign`, {
      splits: splits
        .filter((s) => Number(s.deltaKg) > 0)
        .map((s) => ({ reasonCode: s.reasonCode, deltaKg: Number(s.deltaKg) })),
      note: note.trim() || undefined,
    })
    onDone()
  })

  return (
    <Panel
      title="Assign the missing kilos"
      meta={`${total.toFixed(1)} kg to account for`}
    >
      <div className="panel-body col" style={{ gap: 12 }}>
        {splits.map((s, i) => (
          <div className="row" key={i} style={{ gap: 8 }}>
            <select
              className="input grow"
              value={s.reasonCode}
              onChange={(e) =>
                setSplits((rows) => rows.map((r, j) => (j === i ? { ...r, reasonCode: e.target.value } : r)))
              }
            >
              {REASONS.map((r) => (
                <option key={r.code} value={r.code}>{r.label}</option>
              ))}
            </select>
            <input
              className="input n"
              style={{ width: 110 }}
              type="number"
              step="0.1"
              min="0"
              value={s.deltaKg}
              onChange={(e) =>
                setSplits((rows) => rows.map((r, j) => (j === i ? { ...r, deltaKg: e.target.value } : r)))
              }
              aria-label="Kilos"
            />
            <span className="faint" style={{ fontSize: 'var(--fs-xs)' }}>kg</span>
            {splits.length > 1 && (
              <button
                className="icon-btn"
                onClick={() => setSplits((rows) => rows.filter((_, j) => j !== i))}
                aria-label="Remove this reason"
              >
                ×
              </button>
            )}
          </div>
        ))}

        <div className="spread">
          <button
            className="btn quiet"
            style={{ minHeight: 30 }}
            onClick={() =>
              setSplits((rows) => [...rows, { reasonCode: 'GRADE_OUT', deltaKg: Math.max(remaining, 0).toFixed(1) }])
            }
          >
            Add another reason
          </button>
          <span className={balanced ? 'ok-t' : 'crit-t'} style={{ fontSize: 'var(--fs-sm)', fontWeight: 700 }}>
            {balanced ? 'Balanced' : `${remaining > 0 ? remaining.toFixed(1) : `+${(-remaining).toFixed(1)}`} kg ${remaining > 0 ? 'still unaccounted' : 'over-assigned'}`}
          </span>
        </div>

        <div className="field">
          <label htmlFor="vnote">Note</label>
          <input id="vnote" className="input" value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Who graded it, and what they saw" />
        </div>

        {save.error && <div className="error-text">{save.error.message}</div>}

        <button className="btn" disabled={!balanced || save.busy} onClick={() => void save.run()}>
          {save.busy ? 'Saving…' : 'Assign reasons'}
        </button>
        <p className="hint">
          Reasons must add up to the variance exactly. Losses are coded separately so theft is never
          hidden inside wastage.
        </p>
      </div>
    </Panel>
  )
}
